<script setup lang="ts">
type FactIcon = "tree-pine" | "flower-2" | "cloud-rain" | "leaf";

interface Fact {
  icon: FactIcon;
  title: string;
  note: string;
}

interface ParklandBlockData {
  eyebrow?: string;
  header: string;
  intro?: string;
  paragraphs: string[];
  image?: string;
  image_alt?: string;
  photo_credit?: string;
  photo_credit_url?: string;
  facts?: Fact[];
}

defineProps<{
  block: ParklandBlockData;
}>();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/40 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-emerald-200/25 blur-3xl dark:bg-emerald-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12">
      <!-- Section header: eyebrow + italic h2 + amber divider + optional intro -->
      <header class="mb-8 max-w-3xl md:mb-10">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg lg:text-xl dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <!-- Image RIGHT / text LEFT — flipped vs ExceptionalBlock to give the page visual rhythm.
           md:items-stretch + md:h-full on figure/image → image matches text-column height on desktop. -->
      <div class="grid gap-8 md:grid-cols-12 md:gap-12 md:items-stretch">
        <figure class="md:col-span-6 md:order-2 md:h-full">
          <NuxtImg
            v-if="block.image"
            :src="block.image"
            :alt="block.image_alt ?? block.header"
            width="1400"
            height="1050"
            format="webp"
            loading="lazy"
            sizes="100vw md:50vw"
            class="h-64 w-full rounded-2xl object-cover shadow-lg sm:h-80 md:h-full md:min-h-[24rem]"
          />
          <figcaption
            v-if="block.photo_credit"
            class="mt-2 font-body text-[11px] leading-relaxed text-slate-400 dark:text-slate-500"
          >
            <a
              v-if="block.photo_credit_url"
              :href="block.photo_credit_url"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-sm hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:hover:text-slate-300 dark:focus-visible:ring-offset-slate-900"
            >
              {{ block.photo_credit }}
            </a>
            <span v-else>{{ block.photo_credit }}</span>
          </figcaption>
        </figure>

        <div class="md:col-span-6 md:order-1 flex flex-col gap-4">
          <p
            v-for="(paragraph, i) in block.paragraphs"
            :key="i"
            class="font-body text-base leading-relaxed text-slate-600 sm:text-lg lg:text-xl dark:text-slate-300"
          >
            {{ paragraph }}
          </p>
        </div>
      </div>

      <!-- Nature perks: 2×2 horizontal rows (icon left / text right).
           Uniform emerald tone — distinct from ExceptionalBlock (4×1 amber grid)
           and RhythmBlock (2×2 four-tone grid). -->
      <div
        v-if="block.facts?.length"
        class="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mt-12 md:gap-4"
      >
        <article
          v-for="fact in block.facts"
          :key="fact.icon + fact.title"
          class="flex items-start gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:p-5 dark:border-emerald-500/15 dark:bg-emerald-500/5"
        >
          <span
            class="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          >
            <Icon :name="`lucide:${fact.icon}`" class="size-5" aria-hidden="true" />
          </span>
          <div class="min-w-0 flex-1">
            <h3
              class="font-display text-base italic leading-snug text-slate-900 md:text-lg dark:text-white break-words"
            >
              {{ fact.title }}
            </h3>
            <p
              class="mt-1 font-body text-sm leading-relaxed text-slate-600 dark:text-slate-400"
            >
              {{ fact.note }}
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
